import type { RunState } from '~/types/game'

/**
 * 🗓️⚗️ WEEKLY MODIFIERS (v5.66, designed with Javi): each week's shared
 * seed can carry ONE law of the land — a playtest instrument as much as
 * a novelty. The schedule below is CURATED, not seeded (we are testing
 * hypotheses, not rolling dice), and weeks without an entry are
 * vanilla baselines to compare against.
 *
 * The active modifier is frozen INTO the run at startNewRun
 * (run.weeklyMod), so a mid-week schedule edit never mutates an
 * in-flight run, and determinism holds: the mod is part of the
 * skeleton. Campaign and gauntlet runs never carry one.
 *
 * Import discipline: this module stays pure data + pure functions —
 * battle.ts pulls it into the sim bundles outside Nuxt.
 */

export interface WeeklyModDef {
  id: string
  icon: string
  name: string
  blurb: string
  /** 🖼️ v5.72 (art by Javi): the law's painted scene, shown on the
   * weekly intro card. */
  art: string
  /** Reward/relic def ids never offered while this law holds. */
  excludeOffers?: readonly string[]
  /** Trained-skill ids pulled from the curriculum while it holds. */
  excludeSkills?: readonly string[]
}

/* ⚖️ Iron Week's boss rule: full coverage or nothing. */
export const IRON_BOSS_COVERAGE = 0.999
/* 🌫️ Fog: seconds a fresh mission stays unread on the field. */
export const FOG_REVEAL_SECONDS = 10
/* 💀 Sudden Death: the whole week's health pool. */
export const SUDDEN_DEATH_HP = 10

/** 🖼️ v6.46 (art by Javi): the painting a week with NO law wears. */
export const WEEKLY_VANILLA_ART = '/ui/weekly/normal.webp'

export const WEEKLY_MODS: Record<string, WeeklyModDef> = {
  iron: {
    id: 'iron',
    icon: '⚖️',
    art: '/ui/weekly/iron.webp',
    name: 'Iron Week',
    blurb: 'No dice. Teams always prevail, paying the posted shortfall in HP — and a boss falls only to FULL coverage.',
  },
  fog: {
    id: 'fog',
    icon: '🌫️',
    art: '/ui/weekly/fog.webp',
    name: 'Fog of War',
    blurb: 'Missions arrive unread — their demands surface after 10 seconds on the field. Dispatch blind, or let the clock burn while the fog lifts. Requirements run 15% lighter.',
  },
  sudden: {
    id: 'sudden',
    icon: '💀',
    art: '/ui/weekly/sudden.webp',
    name: 'Sudden Death',
    blurb: 'Ten HP for the whole run — but every wound, whatever its size, costs exactly 1. You can afford precisely nine mistakes.',
  },
  scars: {
    id: 'scars',
    icon: '🩸',
    art: '/ui/weekly/scars.webp',
    name: 'Week of Scars',
    blurb: 'Every victory wounds one of the team — and cleansing wounds at a rest stop hardens them: +1 permanent to the creature’s best element per stack cured.',
  },
  rush: {
    id: 'rush',
    icon: '⏱️',
    art: '/ui/weekly/rush.webp',
    name: 'Rush Hour',
    blurb: 'Mission clocks run at DOUBLE pace, but demands are a quarter lighter. Speed chess: easy fights, no time to think.',
  },
  flood: {
    id: 'flood',
    icon: '🌊',
    art: '/ui/weekly/flood.webp',
    name: 'The Flood',
    blurb: 'Missions arrive half again as fast, but linger longer. The field drowns — triage is the game.',
  },
  freshlegs: {
    id: 'freshlegs',
    icon: '🏃',
    art: '/ui/weekly/freshlegs.webp',
    name: 'Fresh Legs',
    blurb: 'No travel: teams arrive and return instantly. In exchange the clocks run a quarter faster — pure dispatch tempo.',
    // Travel is free all week — the offers that only buy travel are
    // dead picks and leave every pool (per Javi).
    excludeOffers: ['relic-swift-boots', 'relic-warning-bell', 'relic-fast-deployment'],
    excludeSkills: ['trailblazer', 'skim'],
  },
  loadout: {
    id: 'loadout',
    icon: '🎒',
    art: '/ui/weekly/loadout.webp',
    name: 'Loadout Lock',
    blurb: 'The draft deals EIGHT and you keep five — but no recruits will join you all week. Your draft is your destiny.',
  },
  headstart: {
    id: 'headstart',
    icon: '🚀',
    art: '/ui/weekly/headstart.webp',
    name: 'Head Start',
    blurb: 'The team starts at level 5 with a bonus relic; the world starts 30% harder. Late-game feel from minute one.',
  },
  oneschool: {
    id: 'oneschool',
    icon: '🎓',
    art: '/ui/weekly/oneschool.webp',
    name: 'One School',
    blurb: 'A trained skill teaches the WHOLE team at once — but every lesson offers only two choices.',
  },
  twincurses: {
    id: 'twincurses',
    icon: '👿',
    art: '/ui/weekly/twincurses.webp',
    name: 'Twin Curses',
    blurb: 'Every elite carries TWO modifiers. The elite-hunter’s week.',
  },
  prisma: {
    id: 'prisma',
    icon: '🌈',
    art: '/ui/weekly/prisma.webp',
    name: 'Prisma',
    blurb: 'Every mission demands all five elements, spread thin. Rainbow teams only.',
  },
}

/** The curated rotation — Mondays (UTC week keys) → modifier ids.
 * Unlisted weeks are vanilla baselines, on purpose: they are the
 * control group every experiment is compared against. */
export const WEEKLY_SCHEDULE: Record<string, string> = {
  '2026-08-31': 'iron',
  '2026-09-14': 'rush',
  '2026-09-21': 'fog',
  '2026-10-05': 'headstart',
  '2026-10-12': 'flood',
  '2026-10-26': 'prisma',
  '2026-11-02': 'oneschool',
  '2026-11-16': 'freshlegs',
  '2026-11-23': 'loadout',
  '2026-12-07': 'twincurses',
  '2026-12-14': 'scars',
  '2026-12-28': 'sudden',
}

/** The law of a given week (null = vanilla). */
export function weeklyModFor(weekKey: string): WeeklyModDef | null {
  const id = WEEKLY_SCHEDULE[weekKey]
  return id ? WEEKLY_MODS[id] ?? null : null
}

/** The law the RUN was dealt under (frozen at start; null elsewhere). */
export function activeWeeklyMod(run: Pick<RunState, 'mode' | 'weeklyMod'> | null | undefined): WeeklyModDef | null {
  if (!run || run.mode !== 'daily' || !run.weeklyMod) return null
  return WEEKLY_MODS[run.weeklyMod] ?? null
}

export function hasWeeklyMod(run: Pick<RunState, 'mode' | 'weeklyMod'> | null | undefined, id: string): boolean {
  return activeWeeklyMod(run)?.id === id
}

/** 💀 Sudden Death's one law: any wound costs exactly 1. */
export function suddenDeathClamp(run: Pick<RunState, 'mode' | 'weeklyMod'> | null | undefined, amount: number): number {
  if (amount > 0 && hasWeeklyMod(run, 'sudden')) return 1
  return amount
}

/** Generation-side multipliers (missiongen reads these through opts). */
export function weeklyReqMult(modId: string | null | undefined): number {
  return modId === 'rush' ? 0.75 : modId === 'fog' ? 0.85 : modId === 'headstart' ? 1.3 : 1
}
export function weeklyExpireMult(modId: string | null | undefined): number {
  return modId === 'rush' ? 0.5 : modId === 'flood' ? 1.5 : modId === 'freshlegs' ? 0.75 : 1
}
/** 🌊 The Flood: spawn cadence factor (applied to the between-spawn roll). */
export function weeklySpawnMult(modId: string | null | undefined): number {
  return modId === 'flood' ? 0.67 : 1
}
